/**
 * `/forgot-password` view — issue #117.
 *
 * Server returns 202 regardless of whether the email is known (no
 * enumeration oracle — see accounts/routes.ts). The view shows a
 * deliberately ambiguous "if registered, a link is on its way" panel
 * on success.
 */
import { button, card, textInput } from '@dl/ui';
import { div, form, h1, label, main, p, span, text, type Mountable, type Send, type Signal } from '@llui/dom';

import { t } from '../i18n.js';
import { appRouteLink, type AppRoute } from '../router.js';

import { requestPasswordReset } from './api.js';
import {
  clearFormErrors,
  focusFirstError,
  renderAuthSuccess,
  setFieldError,
  setFormError,
  setSubmitting,
} from './forms.js';

async function onSubmit(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  const formEl = event.currentTarget as HTMLFormElement;
  clearFormErrors(formEl);
  const data = new FormData(formEl);
  const email = String(data.get('email') ?? '').trim();
  if (email.length === 0) {
    setFieldError(formEl, 'forgot-email', t('auth.forgot.email_label'));
    focusFirstError(formEl);
    return;
  }
  setSubmitting(formEl, true);
  const result = await requestPasswordReset(email);
  setSubmitting(formEl, false);
  if (result.ok) {
    // Replace form with success message.
    const card = formEl.closest<HTMLElement>('.auth-card');
    if (card) {
      renderAuthSuccess(card, {
        heading: t('auth.forgot.heading'),
        body: t('auth.forgot.sent'),
        bodyTestid: 'forgot-password-sent',
        linkHref: '/sign-in',
        linkText: t('auth.forgot.back_link'),
        linkTestid: 'forgot-password-back-link',
      });
    }
    return;
  }
  setFormError(formEl, result.error);
}

const handleSubmit = (event: Event): void => {
  void onSubmit(event as SubmitEvent);
};

export function viewForgotPassword<Model, Msg>(_state: Signal<Model>, send: Send<Msg>): Mountable {
  return main({ role: 'main', class: 'auth-shell', 'data-testid': 'forgot-password-view' }, [
    card({ variant: 'default', padding: 'xl', className: 'auth-card', children: [
      div({ class: 'auth-card-head' }, [
        h1([text(t('auth.forgot.heading'))]),
        p({ class: 'auth-card-subtitle' }, [text(t('auth.forgot.body'))]),
      ]),
      form(
        {
          class: 'auth-form',
          'data-testid': 'forgot-password-form',
          novalidate: true,
          onSubmit: handleSubmit,
        },
        [
          div({ class: 'auth-form-row' }, [
            label({ for: 'forgot-email' }, [text(t('auth.forgot.email_label'))]),
            textInput({
              type: 'email',
              testid: 'forgot-email',
              attrs: {
                id: 'forgot-email',
                name: 'email',
                autocomplete: 'username',
                required: true,
                'aria-describedby': 'forgot-email-error',
              },
            }),
            span(
              {
                id: 'forgot-email-error',
                'data-error-for': 'forgot-email',
                class: 'auth-field-error',
                role: 'alert',
              },
              [],
            ),
          ]),
          p({ class: 'auth-form-error', 'data-form-error': '', role: 'alert' }, []),
          div({ class: 'auth-form-actions' }, [
            button({
              variant: 'primary',
              type: 'submit',
              testid: 'forgot-password-submit',
              children: [text(t('auth.forgot.submit'))],
            }),
          ]),
        ],
      ),
      div({ class: 'auth-form-links' }, [
        appRouteLink(
          send,
          { kind: 'sign-in' } as AppRoute,
          { 'data-testid': 'forgot-password-back-link' },
          [text(t('auth.forgot.back_link'))],
          (route) => ({ type: 'navigate', route }) as unknown as Msg,
        ),
      ]),
    ] }),
  ]);
}
